import { useEffect, useRef, useState } from 'react'
import { BarChart3, Building2, Check, ChevronsUpDown, Church, Heart, Megaphone, Settings2, Users, Wallet, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { FrenteKey } from '../contract'
import { FRENTES } from '../frentes'

/* ERP · frente atual no topo do menu do Staff. Com mais de uma frente, abre um menu no mesmo jeito do "Trocar de
   espaço" do Brand (mais compacto): ícone, nome e ✓ na atual; Esc, clique fora e setas funcionam. Com uma frente só,
   mostra o nome, sem menu. */

export const FRENTE_ICONS: Record<FrenteKey, LucideIcon> = {
    relacionamento: Heart, captacao: Church, corporativo: Building2, marketing: Megaphone,
    programas: Users, financeiro: Wallet, gestao: BarChart3, tecnologia: Settings2,
}

export function FrenteSwitcher({ frente, frentes, onChange, collapsed }: { frente: FrenteKey; frentes: readonly FrenteKey[]; onChange: (f: FrenteKey) => void; collapsed: boolean }) {
    const def = FRENTES[frente]
    const many = frentes.length > 1
    const Icon = FRENTE_ICONS[frente]
    const [open, setOpen] = useState(false)
    const ref = useRef<HTMLDivElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!open) return
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
        const onDown = (e: PointerEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
        document.addEventListener('keydown', onKey)
        document.addEventListener('pointerdown', onDown)
        // Foco na frente atual ao abrir
        menuRef.current?.querySelector<HTMLButtonElement>('[aria-checked="true"]')?.focus()
        return () => {
            document.removeEventListener('keydown', onKey)
            document.removeEventListener('pointerdown', onDown)
        }
    }, [open])

    const onMenuKey = (e: React.KeyboardEvent) => {
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
        e.preventDefault()
        const items = [...(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitemradio"]') ?? [])]
        const i = items.indexOf(document.activeElement as HTMLButtonElement)
        items[(i + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length]?.focus()
    }

    const pick = (f: FrenteKey) => {
        setOpen(false)
        if (f !== frente) onChange(f)
    }

    const face = (
        <>
            <span aria-hidden="true" className="cf-tile grid size-7 flex-none place-items-center rounded-[8px] bg-tint text-green"><Icon size={15} strokeWidth={1.9} /></span>
            <span className={cn('min-w-0 flex-1 flex-col text-left leading-[1.25]', collapsed ? 'hidden' : 'flex')}>
                <span className="cf-only-wide truncate text-[11.5px] text-muted">Frente</span>
                <span className="truncate text-[13.5px] font-semibold text-fg">{def.label}</span>
            </span>
            {many && !collapsed && <ChevronsUpDown size={16} className="cf-only-wide flex-none text-muted" aria-hidden="true" />}
        </>
    )
    const boxClass = cn('cf-ri cf-frente mb-1.5 flex min-h-11 w-full items-center gap-2.5 rounded-[10px] border border-line px-2.5 py-1.5', collapsed && 'justify-center px-0')

    if (!many) return <div className={boxClass} title={collapsed ? `Frente: ${def.label}` : undefined}>{face}</div>

    return (
        <div ref={ref} className="relative w-full">
            <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={`Frente: ${def.label}. Trocar de frente`}
                title={collapsed ? `Frente: ${def.label}` : undefined}
                onClick={() => setOpen(v => !v)}
                className={cn(boxClass, 'cursor-pointer bg-transparent hover:bg-[var(--hover)]')}
            >
                {face}
            </button>
            {open && (
                <div
                    ref={menuRef}
                    role="menu"
                    aria-label="Trocar de frente"
                    onKeyDown={onMenuKey}
                    className={cn('absolute top-full z-40 mt-1 flex min-w-[220px] flex-col gap-px rounded-[12px] border border-line bg-panel p-1', collapsed ? 'left-0' : 'right-0 left-0')}
                    style={{ boxShadow: '0 12px 32px var(--shadow), 0 2px 6px var(--shadow)' }}
                >
                    <span className="px-2 pt-1 pb-1 text-[11.5px] text-muted">Trocar de frente</span>
                    {frentes.map(f => {
                        const FIcon = FRENTE_ICONS[f]
                        const current = f === frente
                        return (
                            <button
                                key={f}
                                type="button"
                                role="menuitemradio"
                                aria-checked={current}
                                title={FRENTES[f].role}
                                onClick={() => pick(f)}
                                className={cn('flex min-h-8 w-full cursor-pointer items-center gap-2 rounded-[8px] border-0 bg-transparent px-2 text-left text-[13px] text-fg hover:bg-[var(--hover)] focus-visible:bg-[var(--hover)]', current && 'font-semibold')}
                            >
                                <span aria-hidden="true" className="grid size-6 flex-none place-items-center rounded-[7px] border border-line text-muted"><FIcon size={13} strokeWidth={1.9} /></span>
                                <span className="min-w-0 flex-1 truncate">{FRENTES[f].label}</span>
                                {current && <Check size={14} className="flex-none text-green" aria-hidden="true" />}
                            </button>
                        )
                    })}
                </div>
            )}
        </div>
    )
}
